import {
  DecideContractTypeRequestSchema,
  DismissAmendmentSignalRequestSchema,
  ENDPOINTS,
  type ContractTypeProposalsResponse,
  type DecideContractTypeRequest,
  type DecideContractTypeResponse,
  type DismissAmendmentSignalResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * The Admin's type decisions — CLM-110 (FR-35, FR-34, DR-10, CR-08; D-110-2).
 *
 * `fetchContractTypeProposals` is the one read behind the **Type proposals**
 * surface (v2, R-110-22): the undecided agreements by reason, the confirmed
 * families a newer reading disagrees with, and the self-checking totals. No
 * parameters. The response is parsed against the registry schema, whose
 * refinements pin the arithmetic (every reason is counted as listed, the
 * `no_ai_proposal` cap holds, each row's `seen` is the row as listed), so an
 * off-contract body is a `ZodError`, never a list showing a plausible wrong
 * count.
 *
 * `decideContractType` is `POST /v1/contracts/{contract_key}/type`
 * (`admin:configure`): one audited decision on an agreement family. The body
 * is validated `.strict()` before anything is sent — no UPN, no role, no scope
 * — and carries `seen`, the attestation of what the Admin looked at, posted
 * back unchanged from the row. The key travels in the path only, encoded, so
 * either key shape (`SGW-0012`, `SGW-MSA-0003`, D-110-1) reaches the API as
 * written.
 *
 * `dismissAmendmentSignal` is `POST /v1/contracts/{contract_key}/amendment-signal/dismiss`
 * (`admin:configure`, CLM-118 R-118-14): an Admin dismisses a "possibly an
 * amendment" flag. The body is empty — the path names the contract and the
 * session names the Admin — and the response's `audit` is the `0046` row.
 *
 * NFR-06: no `scope` key is ever sent — scope is the API's, from the token;
 * `scope_kind` on the list is how the page learns it.
 */

const LIST = ENDPOINTS['contract-type-proposals'];
const DECIDE = ENDPOINTS['decide-contract-type'];
const DISMISS = ENDPOINTS['dismiss-amendment-signal'];

export async function fetchContractTypeProposals(signal?: AbortSignal): Promise<ContractTypeProposalsResponse> {
  const raw = await apiGet<unknown>(LIST.path, signal === undefined ? {} : { signal });
  return LIST.response.parse(raw);
}

export async function decideContractType(
  request: DecideContractTypeRequest,
  signal?: AbortSignal,
): Promise<DecideContractTypeResponse> {
  const body = DecideContractTypeRequestSchema.parse(request);
  const { contract_key, ...rest } = body;
  const path = DECIDE.path.replace('{contract_key}', encodeURIComponent(contract_key));
  const raw = await apiPost<unknown>(path, rest, signal === undefined ? {} : { signal });
  return DECIDE.response.parse(raw);
}

export async function dismissAmendmentSignal(
  contractKey: string,
  signal?: AbortSignal,
): Promise<DismissAmendmentSignalResponse> {
  const { contract_key } = DismissAmendmentSignalRequestSchema.parse({ contract_key: contractKey });
  const path = DISMISS.path.replace('{contract_key}', encodeURIComponent(contract_key));
  const raw = await apiPost<unknown>(path, {}, signal === undefined ? {} : { signal });
  return DISMISS.response.parse(raw);
}
